import {
  Box,
  Button,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  SimpleGrid,
  Flex,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Textarea,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaIndent } from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import { debounce } from "lodash";
import AutoResizeTextarea from "./AutoResizeTextarea";
import MiniRichTextEditor from "./components/MiniRichTextEditor";
import { unwrapFetchResponse } from "./utils/unwrapApiResponse";
import { useAuth } from "./AuthContext";
import { canEditRecord, withOwnerOrganisation } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";

const defaultContentItem = () => ({
  headingType: "",
  heading: "NEW",
  headingSynonyms: "",
  value: "",
  description: "",
  generalDirection: "",
  fieldDirection: "",
  codeFieldDirection: "",
  editableBy: [],
  adminBy: [],
  dataDefinitions: [
    { dataType: "json", schema: "{}", data: "{}" },
    { dataType: "xml", schema: "<root></root>", data: "<root></root>" },
  ],
});

const defaultSegment = () => ({
  name: "NEW SEGMENT",
  generalDirection: "",
  content: [],
});

const formatJSON = (jsonString) => {
  try {
    const trimmed = jsonString.replace(/^\s+/, "");
    const json = JSON.parse(trimmed);
    return JSON.stringify(json, null, 2);
  } catch (error) {
    console.error("Invalid JSON:", error);
    return jsonString;
  }
};

const formatXML = (xmlString) => {
  try {
    // Remove leading whitespace
    const trimmed = xmlString.replace(/^\s+/, "");
    // Parse XML string to DOM
    const parser = new window.DOMParser();
    const xmlDoc = parser.parseFromString(trimmed, "application/xml");
    // Check for parse errors
    if (xmlDoc.getElementsByTagName("parsererror").length) {
      return xmlString;
    }
    // Serialize DOM back to string with indentation
    const serializer = new window.XMLSerializer();
    const raw = serializer.serializeToString(xmlDoc);

    // Pretty print: add indentation
    const PADDING = "  ";
    let formatted = "";
    const reg = /(>)(<)(\/*)/g;
    let xml = raw.replace(reg, "$1\n$2$3");
    let pad = 0;
    xml.split("\n").forEach((node) => {
      let indent = 0;
      if (node.match(/.+<\/\w[^>]*>$/)) {
        indent = 0;
      } else if (node.match(/^<\/\w/)) {
        if (pad !== 0) pad -= 1;
      } else if (node.match(/^<\w([^>]*[^/])?>.*$/)) {
        indent = 1;
      } else {
        indent = 0;
      }
      formatted += PADDING.repeat(pad) + node + "\n";
      pad += indent;
    });
    return formatted.trim();
  } catch (error) {
    console.error("Invalid XML:", error);
    return xmlString;
  }
};

const DocumentDefinitionsEditor = ({ documentDefinition, isNew }) => {
  const [documentDefinitionHeader, setDocumentDefinitionHeader] = useState({});
  const [segments, setSegments] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isReadOnly = !isNew && !canEditRecord(user, documentDefinition);

  useEffect(() => {
    if (documentDefinition) {
      setDocumentDefinitionHeader({
        ...documentDefinition,
        segments: undefined,
      });
      setSegments(documentDefinition.segments || []);
    }
  }, [documentDefinition]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setDocumentDefinitionHeader((prev) =>
          prev[field] === value ? prev : { ...prev, [field]: value }
        );
      }, 5),
    []
  );

  // Segment CRUD
  const addSegment = () => setSegments((prev) => [...prev, defaultSegment()]);
  const deleteSegment = (idx) =>
    setSegments((prev) => prev.filter((_, i) => i !== idx));
  const handleSegmentChange = (idx, field, value) => {
    setSegments((prev) =>
      prev.map((seg, i) => (i === idx ? { ...seg, [field]: value } : seg))
    );
  };

  // Content CRUD
  const addContent = (segmentIdx) =>
    setSegments((prev) =>
      prev.map((seg, i) =>
        i === segmentIdx
          ? { ...seg, content: [...(seg.content || []), defaultContentItem()] }
          : seg
      )
    );

  const deleteContent = (segmentIdx, contentIdx) =>
    setSegments((prev) =>
      prev.map((seg, i) =>
        i === segmentIdx
          ? {
              ...seg,
              content: (seg.content || []).filter((_, j) => j !== contentIdx),
            }
          : seg
      )
    );

  const handleContentChange = (segmentIdx, contentIdx, field, value) =>
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segmentIdx) return seg;
        const updated = [...(seg.content || [])];
        updated[contentIdx] = { ...updated[contentIdx], [field]: value };
        return { ...seg, content: updated };
      })
    );

  const reorderContent = (segmentIdx, fromIndex, toIndex) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segmentIdx) return seg;
        const content = [...(seg.content || [])];
        const [movedItem] = content.splice(fromIndex, 1);
        content.splice(toIndex, 0, movedItem);
        return { ...seg, content };
      })
    );
  };

  // Data Definition CRUD
  const addDataDefinition = (segmentIdx, contentIdx) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segmentIdx) return seg;
        const updated = [...(seg.content || [])];
        updated[contentIdx] = {
          ...updated[contentIdx],
          dataDefinitions: [
            ...(updated[contentIdx].dataDefinitions || []),
            { dataType: "json", schema: "{}", data: "{}" },
            { dataType: "xml", schema: "<root></root>", data: "<root></root>" },
          ],
        };
        return { ...seg, content: updated };
      })
    );
  };

  const deleteDataDefinition = (segmentIdx, contentIdx, defIdx) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segmentIdx) return seg;
        const updated = [...(seg.content || [])];
        const defs = [...(updated[contentIdx].dataDefinitions || [])].filter(
          (_, j) => j !== defIdx
        );
        updated[contentIdx] = {
          ...updated[contentIdx],
          dataDefinitions: defs,
        };
        return { ...seg, content: updated };
      })
    );
  };

  const handleDataDefinitionChange = (
    segmentIdx,
    contentIdx,
    defIdx,
    field,
    value
  ) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segmentIdx) return seg;
        const updated = [...(seg.content || [])];
        const defs = (updated[contentIdx].dataDefinitions || []).map((def, j) =>
          j === defIdx ? { ...def, [field]: value } : def
        );
        updated[contentIdx] = {
          ...updated[contentIdx],
          dataDefinitions: defs,
        };
        return { ...seg, content: updated };
      })
    );
  };

  const handleEditableByChange = (segmentIdx, contentIdx, value) => {
    // Store the raw string value to allow natural comma input
    handleContentChange(segmentIdx, contentIdx, "editableByRaw", value);

    // Also parse and store as array for backend compatibility
    const list = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    handleContentChange(segmentIdx, contentIdx, "editableBy", list);
  };

  const handleAdminByChange = (segmentIdx, contentIdx, value) => {
    // Store the raw string value to allow natural comma input
    handleContentChange(segmentIdx, contentIdx, "adminByRaw", value);

    // Also parse and store as array for backend compatibility
    const list = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    handleContentChange(segmentIdx, contentIdx, "adminBy", list);
  };

  const handleSave = async () => {
    try {
      const fullDocumentDefinition = {
        ...documentDefinitionHeader,
        segments,
      };

      const url = isNew
        ? "https://documents.poc.mrc.ai/api/definitions"
        : `https://documents.poc.mrc.ai/api/definitions/${documentDefinitionHeader._id}`;

      const response = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isNew ? withOwnerOrganisation(fullDocumentDefinition, user) : fullDocumentDefinition),
      });

      if (!response.ok) {
        throw await errorFromResponse(
          response,
          `Failed to ${isNew ? "create" : "save"}. Status: ${response.status}`
        );
      }

      const result = await unwrapFetchResponse(response);

      toast({
        title: isNew
          ? "Document Definition created"
          : "Document Definition saved",
        description: isNew
          ? "New Document Definition was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/documentdefinitions/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving document definition:", error);
      toast({
        title: "Save failed",
        description:
          error?.message ||
          "An error occurred while saving the document definition.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => navigate("/Poc");

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Navbar />
      <Box position="relative" width="100%" flex="1" overflow="hidden">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10} height="100%" overflowY="auto">
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={1}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={1}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                    aria-label="Back"
                  />
                </Tooltip>
                <Heading size="lg">Edit Document Definition</Heading>
                <Tooltip
                  label="Save Document Definition"
                  aria-label="Save Document Definition Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                    isDisabled={isReadOnly}
                    aria-label="Save"
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <VStack spacing={2} align="stretch">
            {/* Header */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={1}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <UuidPreview uuid={documentDefinitionHeader._id} />
              <SimpleGrid columns={[1, null, 3]} spacing={2} mt={1}>
                <FormControl>
                  <FormLabel fontSize="xs">Name</FormLabel>
                  <Input
                    fontSize="xs"
                    value={documentDefinitionHeader.name || ""}
                    onChange={(e) => handleHeaderChange("name", e.target.value)}
                  />
                </FormControl>

                <CodesetSelect
                  size="xs"
                  label="Type"
                  codesetCode="MRCAI009"
                  value={
                    documentDefinitionHeader.type
                      ? {
                          code: documentDefinitionHeader.type,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("type", val?.code ?? "")
                  }
                />

                <CodesetSelect
                  size="xs"
                  label="Status"
                  codesetCode="MRCAI010"
                  value={
                    documentDefinitionHeader.status
                      ? {
                          code: documentDefinitionHeader.status,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("status", val?.code ?? "")
                  }
                />
              </SimpleGrid>

              <SimpleGrid columns={[1, null, 5]} spacing={2} mt={2}>
                <CodesetSelect
                  size="xs"
                  label="Codify Collection"
                  codesetCode="CODIFY003"
                  value={
                    documentDefinitionHeader.codifyCollection
                      ? {
                          code: documentDefinitionHeader.codifyCollection,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("codifyCollection", val?.code ?? "")
                  }
                />
                <CodesetSelect
                  size="xs"
                  label="Data Standard"
                  codesetCode="CODIFY001"
                  value={
                    documentDefinitionHeader.dataStandard
                      ? {
                          code: documentDefinitionHeader.dataStandard,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("dataStandard", val?.code ?? "")
                  }
                />
                <CodesetSelect
                  size="xs"
                  label="Data Type"
                  codesetCode="CODIFY002"
                  value={
                    documentDefinitionHeader.dataType
                      ? {
                          code: documentDefinitionHeader.dataType,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("dataType", val?.code ?? "")
                  }
                />
                <CodesetSelect
                  size="xs"
                  label="Model Provider"
                  codesetCode="CODIFY005"
                  value={
                    documentDefinitionHeader.modelProvider
                      ? {
                          code: documentDefinitionHeader.modelProvider,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("modelProvider", val?.code ?? "")
                  }
                />
                <CodesetSelect
                  size="xs"
                  label="Model"
                  codesetCode="CODIFY006"
                  value={
                    documentDefinitionHeader.model
                      ? {
                          code: documentDefinitionHeader.model,
                          description: "",
                        }
                      : null
                  }
                  onChange={(val) =>
                    handleHeaderChange("model", val?.code ?? "")
                  }
                />
              </SimpleGrid>

            </Box>

            {/* Segments */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={1}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <HStack alignItems="center">
                <Heading size="sm">Segments</Heading>
                <IconButton
                  icon={<HiOutlinePlus />}
                  size="sm"
                  onClick={addSegment}
                  aria-label="Add segment"
                />
              </HStack>

              <Box mt={1}>
                <Tabs
                  orientation="horizontal"
                  variant="line"
                  colorScheme="linkedin">
                  <TabList>
                    {segments.map((segment, idx) => (
                      <Tab key={`seg-tab-${idx}`}>
                        <HStack spacing={2}>
                          <Text fontSize="xs">
                            {segment.name || `Segment ${idx + 1}`}
                          </Text>
                          <IconButton
                            icon={<HiOutlineTrash />}
                            size="xs"
                            aria-label="Delete segment"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSegment(idx);
                            }}
                          />
                        </HStack>
                      </Tab>
                    ))}
                  </TabList>

                  <TabPanels>
                    {segments.map((segment, segIdx) => (
                      <TabPanel fontSize="xs" key={`seg-panel-${segIdx}`}>
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          borderColor="gray.500"
                          backgroundColor="whiteAlpha.800">
                          <HStack spacing={2}>
                            <FormControl>
                              <FormLabel fontSize="xs">Name</FormLabel>
                              <Input
                                fontSize="xs"
                                value={segment.name || ""}
                                onChange={(e) =>
                                  handleSegmentChange(
                                    segIdx,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="xs">Type</FormLabel>
                              <Input
                                fontSize="xs"
                                value={segment.type || ""}
                                onChange={(e) =>
                                  handleSegmentChange(
                                    segIdx,
                                    "type",
                                    e.target.value
                                  )
                                }
                              />
                            </FormControl>
                          </HStack>

                        </Box>

                        {/* Content */}
                        <Box
                          mt={1}
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          borderColor="gray.500"
                          backgroundColor="whiteAlpha.800">
                          <HStack alignItems="center">
                            <Heading size="sm">Content</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="sm"
                              aria-label="Add content"
                              onClick={() => addContent(segIdx)}
                            />
                          </HStack>

                        <Box
                          mt={1}
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          borderColor="gray.500"
                          backgroundColor="whiteAlpha.800">
                            <Tabs
                              orientation="vertical"
                              variant="line"
                              colorScheme="linkedin">
                              <TabList>
                                {(segment.content || []).map((item, idx) => (
                                  <HStack
                                    key={`content-tab-${segIdx}-${idx}`}
                                    spacing={1}
                                    align="center"
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = "move";
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                                      const toIndex = idx;
                                      if (fromIndex !== toIndex) {
                                        reorderContent(segIdx, fromIndex, toIndex);
                                      }
                                    }}
                                    _hover={{ bg: "gray.50" }}
                                    borderRadius="md"
                                    px={1}>
                                    <Box
                                      color="gray.400"
                                      cursor="grab"
                                      _active={{ cursor: "grabbing" }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData("text/plain", idx.toString());
                                      }}>
                                      <MdDragIndicator size={16} />
                                    </Box>
                                    <Tab fontSize="xs" flex="1">
                                      {item.heading || `Item ${idx + 1}`}
                                    </Tab>
                                  </HStack>
                                ))}
                              </TabList>

                              <TabPanels>
                                {(segment.content || []).map((item, idx) => (
                                  <TabPanel
                                    key={`content-panel-${segIdx}-${idx}`}>
                                    <Tabs
                                      orientation="horizontal"
                                      variant="soft-rounded"
                                      colorScheme="linkedin">
                                      <TabList>
                                        <Tab fontSize="xs">Direction</Tab>

                                      </TabList>

                                      <TabPanels>
                                        {/* Direction */}
                                        <TabPanel>
                                          <SimpleGrid columns={2} spacing={2}>
                                            <FormControl>
                                              <FormLabel fontSize="xs">
                                                Heading
                                              </FormLabel>
                                              <Input
                                                fontSize="xs"
                                                value={item.heading || ""}
                                                onChange={(e) =>
                                                  handleContentChange(
                                                    segIdx,
                                                    idx,
                                                    "heading",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </FormControl>
                                            <FormControl>
                                              <FormLabel fontSize="xs">
                                                Heading Type
                                              </FormLabel>
                                              <Input
                                                fontSize="xs"
                                                value={item.headingType || ""}
                                                onChange={(e) =>
                                                  handleContentChange(
                                                    segIdx,
                                                    idx,
                                                    "headingType",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </FormControl>
                                          </SimpleGrid>
                                          <FormControl>
                                            <FormLabel fontSize="xs">
                                              Heading Synonyms
                                            </FormLabel>
                                            <AutoResizeTextarea
                                              fontSize="xs"
                                              resize="vertical"
                                              minHeight="30px"
                                              value={item.headingSynonyms || ""}
                                              onChange={(e) =>
                                                handleContentChange(
                                                  segIdx,
                                                  idx,
                                                  "headingSynonyms",
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl>
                                            <FormLabel fontSize="xs">
                                              Description
                                            </FormLabel>
                                            <AutoResizeTextarea
                                              fontSize="xs"
                                              resize="vertical"
                                              minHeight="30px"
                                              value={item.description || ""}
                                              onChange={(e) =>
                                                handleContentChange(
                                                  segIdx,
                                                  idx,
                                                  "description",
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>

                                          <FormControl>
                                            <FormLabel fontSize="xs">
                                              Default Value
                                            </FormLabel>
                                            <MiniRichTextEditor
                                              value={item.value || ""}
                                              onChange={(newValue) =>
                                                handleContentChange(
                                                  segIdx,
                                                  idx,
                                                  "value",
                                                  newValue
                                                )
                                              }
                                              placeholder="Enter default value with formatting..."
                                            />
                                          </FormControl>

                                          <FormControl>
                                            <FormLabel fontSize="xs">
                                              Editable By (comma-separated)
                                            </FormLabel>
                                            <AutoResizeTextarea
                                              fontSize="xs"
                                              resize="vertical"
                                              minHeight="30px"
                                              value={
                                                item.editableByRaw !== undefined
                                                  ? item.editableByRaw
                                                  : (item.editableBy || []).join(", ")
                                              }
                                              onChange={(e) =>
                                                handleEditableByChange(
                                                  segIdx,
                                                  idx,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>

                                          <FormControl>
                                            <FormLabel fontSize="xs">
                                              Admin By (comma-separated)
                                            </FormLabel>
                                            <AutoResizeTextarea
                                              fontSize="xs"
                                              resize="vertical"
                                              minHeight="30px"
                                              value={
                                                item.adminByRaw !== undefined
                                                  ? item.adminByRaw
                                                  : (item.adminBy || []).join(", ")
                                              }
                                              onChange={(e) =>
                                                handleAdminByChange(
                                                  segIdx,
                                                  idx,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>

                                          <HStack justify="flex-end" mt={4}>
                                            <IconButton
                                              icon={<HiOutlineTrash />}
                                              size="xs"
                                              aria-label="Delete content"
                                              onClick={() =>
                                                deleteContent(segIdx, idx)
                                              }
                                            />
                                          </HStack>
                                        </TabPanel>

                                      </TabPanels>
                                    </Tabs>
                                  </TabPanel>
                                ))}
                              </TabPanels>
                            </Tabs>
                          </Box>
                        </Box>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              </Box>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentDefinitionsEditor;
